import { useCallback, useEffect, useRef, useState } from "react";
import { Plus, Trash2, Upload } from "lucide-react";
import { api, apiError, mediaUrl } from "@/lib/api";
import { AdminButton, AdminCard, AdminField, EmptyState, adminInput } from "@/components/admin/ui";

const LABELS = [
  "FOTO",
  "HERO PHOTO",
  "BORGO PHOTO",
  "DISH PHOTO",
  "INTERIOR PHOTO",
  "COURTYARD PHOTO",
  "CHEF / KITCHEN PHOTO",
];

export default function AdminGallery() {
  const [items, setItems] = useState([]);
  const [error, setError] = useState("");
  const [uploadingId, setUploadingId] = useState(null);
  const fileInputs = useRef({});

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/gallery");
      setItems(data);
    } catch (err) {
      setError(apiError(err));
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const save = async (item, patch) => {
    try {
      const body = { ...patch };
      if ("image" in body && body.image === null) {
        delete body.image;
        body.clear_image = true;
      }
      await api.put(`/admin/gallery/${item.id}`, body);
      load();
    } catch (err) {
      setError(apiError(err));
    }
  };

  const addSlot = async () => {
    try {
      await api.post("/admin/gallery", {
        image: null,
        placeholder_label: "BORGO PHOTO",
        alt: "",
        category: "borgo",
        order: items.length,
        visible: true,
      });
      load();
    } catch (err) {
      setError(apiError(err));
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/admin/gallery/${id}`);
      load();
    } catch (err) {
      setError(apiError(err));
    }
  };

  const upload = async (item, file) => {
    if (!file) return;
    setUploadingId(item.id);
    setError("");
    try {
      const body = new FormData();
      body.append("file", file);
      const { data } = await api.post("/admin/uploads", body, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      await save(item, { image: data.url });
    } catch (err) {
      setError(apiError(err, "Caricamento non riuscito."));
    } finally {
      setUploadingId(null);
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-gallery">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-[0.62rem] uppercase tracking-[0.2em] text-sage">Contenuti</p>
          <h1 className="mt-2 font-serif text-3xl md:text-4xl text-wood">Galleria</h1>
          <p className="mt-3 max-w-2xl text-sm text-foreground/65">
            Carica le fotografie reali dell'osteria e del borgo. Finché non c'è un'immagine, sul sito
            compare il segnaposto con l'etichetta scelta.
          </p>
        </div>
        <AdminButton variant="wood" onClick={addSlot} data-testid="gallery-add">
          <Plus size={14} /> Nuovo spazio
        </AdminButton>
      </div>

      {error && <p className="border border-destructive/40 bg-destructive/5 px-4 py-3 text-sm text-destructive" data-testid="gallery-error">{error}</p>}

      {items.length === 0 ? (
        <EmptyState title="Galleria vuota" description="Aggiungi il primo spazio immagine." />
      ) : (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {items.map((item) => (
            <AdminCard key={item.id} testId={`gallery-item-${item.id}`}>
              <div className="aspect-[4/3] bg-beige border border-border flex items-center justify-center overflow-hidden">
                {item.image ? (
                  <img src={mediaUrl(item.image)} alt={item.alt} className="h-full w-full object-cover" loading="lazy" />
                ) : (
                  <span className="text-[0.62rem] uppercase tracking-[0.18em] text-wood/50">{item.placeholder_label}</span>
                )}
              </div>
              <div className="mt-4 space-y-3">
                <AdminField label="Etichetta segnaposto">
                  <select
                    value={item.placeholder_label}
                    onChange={(e) => save(item, { placeholder_label: e.target.value })}
                    className={adminInput}
                    data-testid={`gallery-label-${item.id}`}
                  >
                    {LABELS.map((l) => (
                      <option key={l} value={l}>
                        {l}
                      </option>
                    ))}
                  </select>
                </AdminField>
                <AdminField label="Testo alternativo (alt)">
                  <input
                    defaultValue={item.alt}
                    onBlur={(e) => e.target.value !== item.alt && save(item, { alt: e.target.value })}
                    className={adminInput}
                    data-testid={`gallery-alt-${item.id}`}
                  />
                </AdminField>
                <div className="grid grid-cols-2 gap-3">
                  <AdminField label="Categoria">
                    <input
                      defaultValue={item.category}
                      onBlur={(e) => e.target.value !== item.category && save(item, { category: e.target.value })}
                      className={adminInput}
                    />
                  </AdminField>
                  <AdminField label="Ordine">
                    <input
                      type="number"
                      defaultValue={item.order}
                      onBlur={(e) => Number(e.target.value) !== item.order && save(item, { order: Number(e.target.value) })}
                      className={adminInput}
                    />
                  </AdminField>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    ref={(el) => (fileInputs.current[item.id] = el)}
                    onChange={(e) => upload(item, e.target.files?.[0])}
                    data-testid={`gallery-file-${item.id}`}
                  />
                  <AdminButton
                    variant="outline"
                    onClick={() => fileInputs.current[item.id]?.click()}
                    disabled={uploadingId === item.id}
                    data-testid={`gallery-upload-${item.id}`}
                  >
                    <Upload size={13} /> {uploadingId === item.id ? "Carico…" : "Carica foto"}
                  </AdminButton>
                  <AdminButton variant="ghost" onClick={() => save(item, { visible: !item.visible })}>
                    {item.visible ? "Nascondi" : "Mostra"}
                  </AdminButton>
                  {item.image && (
                    <AdminButton variant="ghost" onClick={() => save(item, { image: null })}>
                      Rimuovi foto
                    </AdminButton>
                  )}
                  <AdminButton variant="danger" className="ml-auto" onClick={() => remove(item.id)} data-testid={`gallery-delete-${item.id}`}>
                    <Trash2 size={13} />
                  </AdminButton>
                </div>
              </div>
            </AdminCard>
          ))}
        </div>
      )}
    </div>
  );
}
